import {ArrowPathIcon} from "@heroicons/react/20/solid";
import {getFormattedDateTime, extractErrorMessage, NoticeEntity, useLazySearchUserNoticesQuery, usePagedRtkQuery} from "@ots-frontend/ots-data";
import {useMemo} from "react";
import {OtsIconActionButton} from "../components/OtsButton";
import {OtsDrawer} from "../components/OtsDrawer";
import {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {NoticeList, NoticeListItem} from "./NoticeList";

export type ConnectedNoticeDrawerProps = {
  open: boolean;
  onClose: () => void;
  /** Formats a notice's dates in the app's time zone. */
  formatDateTime?: (dttm: number) => string;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

const defaultFormat = (dttm: number) => getFormattedDateTime(dttm);

export const toNoticeListItem = (notice: NoticeEntity, formatDateTime: (dttm: number) => string = defaultFormat): NoticeListItem => {
  const expired = notice.isExpired();
  return {
    id: notice.id,
    title: notice.title,
    body: notice.bodyText,
    severity: notice.severity.code,
    category: notice.category.displayValue?.defaultMessage ?? notice.category.code,
    createdAt: notice.createDttm != null ? formatDateTime(notice.createDttm) : undefined,
    createdBy: notice.createdByName,
    expiry: notice.expiresDttm != null ? `${expired ? "Expired" : "Expires"} ${formatDateTime(notice.expiresDttm)}` : undefined,
    expired,
    scopes: [
      ...(notice.scopedRegionNames ?? []).map(label => ({kind: "region" as const, label})),
      ...(notice.scopedCompanyNames ?? []).map(label => ({kind: "company" as const, label})),
      ...(notice.scopedAudienceCapabilities ?? []).map(capability => ({
        kind: "audience" as const,
        label: capability.displayValue?.defaultMessage ?? capability.code,
      })),
    ],
  };
};

/** The signed-in person's active notices in a drawer, loaded a page at a time while it is open. */
export const ConnectedNoticeDrawer = ({open, onClose, formatDateTime, brand, colorScheme}: ConnectedNoticeDrawerProps) => {
  const [trigger] = useLazySearchUserNoticesQuery();
  const args = useMemo(() => ({active: true}), []);
  const {items, total, hasMore, isFetching, error, refresh, loadMore} = usePagedRtkQuery({
    args,
    pageSize: 25,
    resetKey: "notices",
    enabled: open,
    trigger,
    mapItem: NoticeEntity.from,
    getId: (notice) => notice.id,
  });
  const notices = useMemo(() => items.map(notice => toNoticeListItem(notice, formatDateTime)), [items, formatDateTime]);

  return (
    <OtsDrawer
      open={open}
      onClose={onClose}
      title="Notices"
      description={`${total} active`}
      brand={brand}
      colorScheme={colorScheme}
      actions={<OtsIconActionButton variant="text" size="compact" disabled={isFetching} icon={<ArrowPathIcon className={isFetching ? "animate-spin" : undefined}/>} label="Refresh notices" onClick={refresh}/>}
      footer="Certain notices may also be sent by text message, depending on your notification templates.">
      <NoticeList
        notices={notices}
        isLoading={isFetching}
        error={error ? extractErrorMessage(error) : undefined}
        hasMore={hasMore}
        onLoadMore={loadMore}
        onRetry={refresh}/>
    </OtsDrawer>
  );
};
